import { useState } from "react";
import {
  Badge, Button, Card, CardSection, Checkbox, Field, Input, Slider, UploadField, resolveAssetUrl,
} from "@/components/ui";
import type { FaceArea, FrameConfig } from "@/lib/types/config";
import { useBuilder } from "../BuilderContext";
import { FaceAreaEditor } from "../components/FaceAreaEditor";
import { IconPlus, IconTrash } from "../components/icons";
import { cn } from "@/lib/utils/cn";

/**
 * Frame overlays and the result-screen QR code.
 *
 * Several frames can be configured; the server picks one at random per
 * generation, which is how an event gets variety without the guest choosing.
 */
export function FrameQrTab() {
  const { draft, update } = useBuilder();
  const frames = draft.frames ?? (draft.frame ? [draft.frame] : []);
  const qr = draft.qr_code ?? {};
  const [selected, setSelected] = useState(0);

  const frame: FrameConfig | undefined = frames[selected];

  function setFrames(next: FrameConfig[]) {
    // `frame` (singular) is the legacy single-overlay shape. Keep it mirrored to
    // the first entry so an older booth build still renders something sane.
    //
    // `null`, not `undefined`, when the list is empty. This is the bug that made
    // "I removed the frame and it's still on my photos" happen: `undefined` is
    // dropped by JSON.stringify, so the key never reached the server, the draft
    // merged over a config that still had the old `frame`, and the renderer
    // happily fell back to it. An explicit null is the only way to say "gone".
    update((current) => ({
      ...current,
      frames: next,
      frame: next.length > 0 ? next[0] : null,
    }));
  }

  function patchFrame(patch: Partial<FrameConfig>) {
    setFrames(frames.map((item, index) => (index === selected ? { ...item, ...patch } : item)));
  }

  /**
   * Overlay changed — measure it and record its real pixel size.
   *
   * native_width/height decide the shape of the booth's camera preview and of
   * the rendered photo. They used to default to 1024x1024 and only change if the
   * operator typed the numbers in, so uploading a portrait frame left the system
   * believing it was square: the preview came out the wrong shape and the photo
   * was cropped to match. Measuring the file removes that whole class of
   * mistake. The fields stay editable for anyone who wants to override.
   */
  function setOverlay(path: string | undefined) {
    patchFrame({ overlay_path: path });
    if (!path) return;

    const probe = new Image();
    probe.onload = () => {
      if (!probe.naturalWidth || !probe.naturalHeight) return;
      patchFrame({
        overlay_path: path,
        native_width: probe.naturalWidth,
        native_height: probe.naturalHeight,
      });
    };
    probe.src = resolveAssetUrl(path) ?? path;
  }

  function addFrame() {
    setFrames([...frames, { enabled: true, padding: 0.94, native_width: 1024, native_height: 1024 }]);
    setSelected(frames.length);
  }

  function removeFrame(index: number) {
    const next = frames.filter((_, i) => i !== index);
    setFrames(next);
    setSelected(Math.max(0, Math.min(selected, next.length - 1)));
  }

  const setQr = (patch: Partial<typeof qr>) =>
    update((current) => ({ ...current, qr_code: { ...current.qr_code, ...patch } }));

  return (
    <div className="flex flex-col gap-8">
      <CardSection
        eyebrow="Overlays"
        title="Frame overlays"
        description="Add one or more frame overlays. One is picked at random for each photo generation."
      >
        <Card className="flex flex-col gap-5 p-5">
          <div className="flex flex-wrap items-center gap-3">
            {frames.map((item, index) => (
              <button
                key={index}
                onClick={() => setSelected(index)}
                className={cn(
                  "grid h-16 w-16 place-items-center overflow-hidden rounded-lg border text-xs transition-colors",
                  index === selected ? "border-accent" : "border-ink-600 hover:border-ink-500",
                )}
              >
                {item.overlay_path ? (
                  <img src={resolveAssetUrl(item.overlay_path)} alt="" className="h-full w-full object-cover" />
                ) : (
                  <span className="text-haze-400">F{index + 1}</span>
                )}
              </button>
            ))}
            <button
              onClick={addFrame}
              className="grid h-16 w-16 place-items-center rounded-lg border border-dashed border-ink-600 text-haze-400 transition-colors hover:border-accent hover:text-accent"
              aria-label="Add frame overlay"
            >
              <IconPlus size={16} />
            </button>
          </div>

          {frame ? (
            <>
              <div className="flex items-center justify-between gap-3">
                <Checkbox
                  checked={frame.enabled ?? true}
                  onChange={(value) => patchFrame({ enabled: value })}
                  label="Enable photo frame overlay"
                />
                <Button
                  variant="ghost" size="sm"
                  onClick={() => removeFrame(selected)}
                  className="text-haze-400 hover:text-danger"
                >
                  <IconTrash size={14} /> Remove
                </Button>
              </div>

              <UploadField
                label="Frame overlay image"
                value={frame.overlay_path}
                onChange={setOverlay}
                hint="PNG with a transparent cutout where the guest's photo should show through. Its size is measured automatically."
              />

              <Slider
                label="Frame padding"
                min={0} max={1} step={0.01}
                value={frame.padding ?? 0.94}
                onChange={(value) => patchFrame({ padding: value })}
                hint="Controls how much of the frame overlay is visible. Higher values show more of the frame edge."
              />

              <div className="grid gap-5 sm:grid-cols-2">
                <Field
                  label="Frame native width (px)"
                  hint="Read from the uploaded image. These two also set the shape of the booth camera — change them only if you know why."
                >
                  <Input
                    type="number" min={256} step={1}
                    value={frame.native_width ?? 1024}
                    onChange={(e) => patchFrame({ native_width: Number(e.target.value) })}
                  />
                </Field>
                <Field label="Frame native height (px)">
                  <Input
                    type="number" min={256} step={1}
                    value={frame.native_height ?? 1024}
                    onChange={(e) => patchFrame({ native_height: Number(e.target.value) })}
                  />
                </Field>
              </div>

              <p className="rounded-lg bg-ink-800 px-3 py-2 text-xs leading-relaxed text-haze-400">
                These dimensions turn the fractional face area into pixels for AI generation. The model
                renders only the cutout region, and the server reassembles the full frame for printing.
                {frame.face_area && (
                  <> Current render size:{" "}
                    <span className="font-mono text-accent">{renderSize(frame)}</span>.
                  </>
                )}
              </p>
            </>
          ) : (
            <p className="text-sm text-haze-400">
              No frame overlays configured. Generations are used as-is, with no frame composited around them.
            </p>
          )}
        </Card>
      </CardSection>

      {frame && (
        <CardSection eyebrow="Face area" title="Face cutout position">
          <Card className="p-5">
            <FaceAreaEditor
              overlayUrl={frame.overlay_path}
              value={frame.face_area}
              onChange={(area: FaceArea) => patchFrame({ face_area: area })}
            />
          </Card>
        </CardSection>
      )}

      <CardSection
        eyebrow="Share"
        title="QR code"
        description="A QR code shown on the result screen so guests can download or share their photos."
      >
        <Card className="flex flex-col gap-5 p-5">
          <Checkbox
            checked={qr.enabled ?? true}
            onChange={(value) => setQr({ enabled: value })}
            label="Enable QR code display"
          />

          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="QR code label">
              <Input
                placeholder="Scan to download"
                value={qr.label ?? ""}
                onChange={(e) => setQr({ label: e.target.value })}
              />
            </Field>
            <Field label="QR code size (px)">
              <Input
                type="number" min={32} max={512}
                value={qr.size ?? 56}
                onChange={(e) => setQr({ size: Number(e.target.value) })}
              />
            </Field>
          </div>

          <Checkbox
            checked={qr.burn_into_print ?? true}
            onChange={(value) => setQr({ burn_into_print: value })}
            label="Burn the QR code into printed photos"
            hint="Lets a guest re-download their photo from the print itself. Turn off for a clean print."
          />

          <fieldset className="flex flex-col gap-4">
            <legend className="label-eyebrow mb-1">QR position on frame</legend>
            <p className="text-xs text-haze-400">Fractional (0–1) of the frame's dimensions.</p>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="X">
                <Input
                  type="number" min={0} max={1} step={0.01}
                  value={qr.position?.x ?? 0.5}
                  onChange={(e) => setQr({ position: { x: Number(e.target.value), y: qr.position?.y ?? 0.9 } })}
                />
              </Field>
              <Field label="Y">
                <Input
                  type="number" min={0} max={1} step={0.01}
                  value={qr.position?.y ?? 0.9}
                  onChange={(e) => setQr({ position: { x: qr.position?.x ?? 0.5, y: Number(e.target.value) } })}
                />
              </Field>
            </div>
          </fieldset>

          <div className="flex items-center gap-2">
            <Badge tone="neutral">Preview</Badge>
            <span className="text-xs text-haze-400">
              The QR points at each guest's own share link, generated per photo.
            </span>
          </div>
        </Card>
      </CardSection>
    </div>
  );
}

/** Mirrors Rails' DimensionResolver so operators see what the model will render. */
function renderSize(frame: FrameConfig): string {
  const nativeWidth = frame.native_width ?? 1024;
  const nativeHeight = frame.native_height ?? 1024;
  const area = frame.face_area;
  if (!area) return `${nativeWidth}×${nativeHeight}`;

  let fractionWidth = area.width ?? 0;
  let fractionHeight = area.height ?? 0;

  if (area.points?.length) {
    const xs = area.points.map((point) => point.x);
    const ys = area.points.map((point) => point.y);
    fractionWidth = Math.max(...xs) - Math.min(...xs);
    fractionHeight = Math.max(...ys) - Math.min(...ys);
  }

  const width = Math.round(fractionWidth * nativeWidth);
  const height = Math.round(fractionHeight * nativeHeight);

  // The provider rejects anything under 256px, and the server falls back.
  if (width < 256 || height < 256) return "1024×1024 (area too small, using fallback)";
  return `${width}×${height}`;
}
